<template>
  <div>
    <div v-if="searchQuery && filteredRowsToIterate.length == 0"><b-alert show variant="info">No requirements found for your search query</b-alert></div>
    <table class="table table-bordered" v-else-if="rows.length > 0">
      <thead>
      <tr>
        <th v-for="header in headerRow">{{ header }}</th>
        <th v-for="issueType in selectedIssueTypeColumns" v-bind:id="issueType.name">{{ issueType.name }}</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="row in filteredRowsToIterate">
        <td v-for="requirement in row.requirements">
          <div v-if="requirement.requirement_id != null">
            <span v-if="requirement.atlassian_status_id != null && statusMap.has(requirement.atlassian_status_id.toString())"
              :class="getLozengeClassFromStatusCategory(statusMap.get(requirement.atlassian_status_id.toString()).statusCategory.key)"
                  v-b-popover.hover="statusMap.get(requirement.atlassian_status_id.toString()).name"></span>
              <a class="matrix-item-id" :href="getNewTabLink(requirement.requirement_id)" target="_blank">{{ requirement.prefix }}{{requirement.display_id ? requirement.display_id : requirement.requirement_id}}</a>
            <div class="matrix-item-name">
              {{ requirement.name }}
            </div>
          </div>
          <div v-else>-</div>
        </td>
        <td v-for="issueType in selectedIssueTypeColumns" v-bind:headers="issueType.name">
          <!-- filter for requirements that match this issuetype -->
          <div v-for="issue in row.issues" v-if="issueLookup[issue.atlassian_issue_id] && issueLookup[issue.atlassian_issue_id].fields.issuetype.id === issueType.id">
            <span :class="getLozengeClassFromStatusCategory(issueLookup[issue.atlassian_issue_id].fields.status.statusCategory.key)"
                  v-b-popover.hover="issueLookup[issue.atlassian_issue_id].fields.status.name"></span>
            <a class="matrix-item-id" target="_blank" :href="`${$auth.user().atlassian_base_url}/browse/${issueLookup[issue.atlassian_issue_id].key}`">{{ issueLookup[issue.atlassian_issue_id].key }}</a>
            <div class="matrix-item-name">
              {{ issueLookup[issue.atlassian_issue_id].fields.summary }}&nbsp;
            </div>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
    <div v-else-if="loading">
      <div class="d-flex justify-content-center align-items-center">
        <icon  name="spinner" pulse scale="5"></icon>
      </div>
    </div>
    <div v-else>
      <b-alert show variant="info">Please create at least one requirement to view the traceability matrix</b-alert>
    </div>
  </div>
</template>

<script>
  import getDataFromAtlassianMixin from '@/mixins/getDataFromAtlassian'
  import utilsMixin from '@/mixins/utils'
  export default {
    name: 'TraceabilityMatrixGrid',
    computed: {
      currentProject () {
        return this.$store.state.currentProject
      },
      filteredRowsToIterate () {
        let data = this.rowsToIterate
        let filterKey = this.searchQuery && this.searchQuery.toLowerCase()
        if (filterKey) {
          data = data.filter(row => {
            return row.requirements.some(requirement => {
              return Object.keys(requirement).some(key => {
                return String(requirement[key]).toLowerCase().indexOf(filterKey) > -1
              })
            })
          })
        }
        return data
      }
    },
    props: {
      searchQuery: String,
      selectedIssueTypeColumns: Array
    },
    mixins: [getDataFromAtlassianMixin, utilsMixin],
    watch: {
      currentProject (currentValue) {
        if (this.currentProject) {
          this.clearData()
          this.getRequirements()
        }
      }
    },
    data () {
      return {
        baseUrl: null,
        combinedIssues: [],
        headerRow: [],
        issueLookup: {},
        loading: false,
        rows: [],
        rowsToIterate: [],
        statusMap: new Map()
      }
    },
    methods: {
      clearData () {
        this.headerRow = []
        this.issueLookup = {}
        this.rows = []
        this.rowsToIterate = []
      },
      combineIssues () {
        let issueArray = []
        // Combine all issues from requirements to get all the IDs, to make a single request to Atlassian
        for (let row of this.rows) {
          for (let iss of row.issues) {
            issueArray.push(iss.atlassian_issue_id)
          }
        }
        // This is some new ES6 stuff that creates a unique array
        this.combinedIssues = [...new Set(issueArray)]
      },
      getBaseUrl () {
        window.AP.getLocation(location => {
          const url = new URL(location)
          this.baseUrl = url.origin
        })
      },
      getIssueDataFromAtlassian () {
        if (this.combinedIssues.length > 0) {
          // TODO - refactor this call.  If this includes an issue ID that is deleted, the call to Atlassian
          // will fail, and the whole trace matrix will fail to show up
          // it often doesn't even show up in sentry, but this is one of them: https://sentry.io/organizations/purple-drapes/issues/1797298047/?project=5248638&query=is%3Aunresolved
          this.getIssueDataFromAtlassianPaginated(this.combinedIssues, null, 50)
        } else {
          this.rowsToIterate = this.rows
        }
      },
      getLozengeClassFromStatusCategory (statusCategoryKey) {
        switch (statusCategoryKey) {
          case 'new': {
            // AKA To Do
            return 'rm-lozenge'
          }
          case 'done': {
            // AKA Done
            return 'rm-lozenge rm-lozenge-success'
          }
          case 'indeterminate': {
            // AKA In Progress
            return 'rm-lozenge rm-lozenge-current'
          }
          default: {
            return 'rm-lozenge'
          }
        }
      },
      getNewTabLink (requirementId) {
        return `${this.baseUrl}/plugins/servlet/ac/${process.env.ADDON_DESCRIPTOR_KEY}/rmcloud?ac.projectId=${this.currentProject.id}&ac.requirementId=${requirementId}`
      },
      getRequirements () {
        this.loading = true
        this.$http.get(
          `/api/requirement/matrix?atlassian_project_id=${this.currentProject.id}`, {
            params: {
              atlassian_project_id: this.currentProject.id
            }
          }
        ).then(response => {
          this.rows = response.data.rows
          this.requirementTypes = response.data.requirement_types
          this.headerRow = response.data.header_row
          this.combineIssues()
          this.getIssueDataFromAtlassian()
          this.loading = false
        }, response => {
          console.log('error')
        })
      },
      setIssues (response, readyToRender) {
        for (let iss of response.issues) {
          this.issueLookup[iss.id] = iss
        }
        // This hack delays the rendering in the template until after the issueLookup has been created
        if (readyToRender) {
          this.rowsToIterate = this.rows
        }
      },
      setStatusOptions (response) {
        // Add to Map for easy lookup
        for (let status of response) {
          this.statusMap.set(status.id, status)
        }
      }
    },
    mounted () {
      this.getBaseUrl()
      this.getRequirements()
      this.getAllStatuses()
    }
  }
</script>

<style scoped>
.matrix-item-id {
  font-size: .8rem;
}
.matrix-item-name {
  font-size: .8rem;
}
</style>



// WEBPACK FOOTER //
// TraceabilityMatrixGrid.vue?42d5f417